import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Upload, AlertCircle, FileJson } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { validateImportFile } from '@/lib/workflows/export-import'
import { useWorkflowLifecycleStore } from '@/stores/workflow-lifecycle-store'
import { cn } from '@/lib/utils'
import type { WorkflowExport } from '@/schemas/workflow-export'

interface ImportWorkflowDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
}

export function ImportWorkflowDialog({
  open,
  onOpenChange,
  projectId,
}: ImportWorkflowDialogProps) {
  const { t } = useTranslation('workflows')
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [preview, setPreview] = useState<WorkflowExport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const importWorkflow = useWorkflowLifecycleStore((s) => s.importWorkflow)
  const importing = useWorkflowLifecycleStore((s) => s.importing)

  const reset = () => {
    setPreview(null)
    setError(null)
    setDragOver(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleFile = async (file: File) => {
    setError(null)
    const res = await validateImportFile(file)
    if (res.ok) {
      setPreview(res.data)
      return
    }
    // All client-side validation errors surface the same user copy
    // per UI-SPEC: actionable "Arquivo inválido" message.
    setError(t('lifecycle.import.invalidFile'))
    setPreview(null)
  }

  const onDrop: React.DragEventHandler = (e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) void handleFile(f)
  }

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0]
    if (f) void handleFile(f)
  }

  const handleImport = async () => {
    if (!preview) return
    const res = await importWorkflow(projectId, preview)
    if (!res) {
      toast.error(t('lifecycle.import.invalidFile'))
      return
    }
    onOpenChange(false)
    reset()
    if (res.needs_attention_nodes.length > 0) {
      toast.warning(
        t('lifecycle.import.successNeedsAttention', {
          count: res.needs_attention_nodes.length,
        })
      )
    } else {
      toast.success(t('lifecycle.import.successClean'))
    }
    navigate(`/workflows/${res.workflow.id}`)
  }

  const handleOpenChange = (o: boolean) => {
    onOpenChange(o)
    if (!o) reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="glass border-gray-700/50 sm:max-w-[520px]"
        data-testid="import-workflow-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-gray-100">
            {t('lifecycle.import.confirmTitle')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('lifecycle.import.confirmBody')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="import-dialog-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!preview ? (
          <div
            className={cn(
              'border-2 border-dashed rounded-lg h-40 flex flex-col items-center justify-center cursor-pointer transition-colors',
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-primary/30 hover:border-primary/50'
            )}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                inputRef.current?.click()
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('lifecycle.import.cta')}
            data-testid="import-dropzone"
          >
            <Upload className="h-8 w-8 text-primary mb-2" />
            <p className="text-sm text-muted-foreground text-center px-4">
              Arraste um arquivo JSON ou clique para selecionar
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={onInputChange}
              data-testid="import-file-input"
            />
          </div>
        ) : (
          <div
            className="border border-gray-700/50 rounded-lg p-4 bg-white/5"
            data-testid="import-preview"
          >
            <div className="flex items-center gap-2 mb-2 text-gray-200">
              <FileJson className="h-4 w-4" />
              <strong>{preview.name}</strong>
            </div>
            <p className="text-sm text-muted-foreground">
              {preview.description ?? '—'}
            </p>
            <div className="text-xs text-muted-foreground mt-2">
              {preview.pattern_type} · {preview.nodes.length} nó(s) ·{' '}
              {preview.edges.length} aresta(s)
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="border-gray-700/50 text-gray-300"
          >
            Cancelar
          </Button>
          <Button
            variant="action-indigo"
            disabled={!preview || importing}
            onClick={handleImport}
            data-testid="import-confirm-cta"
          >
            {importing ? '...' : t('lifecycle.import.confirmCta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
